import React, { useState } from "react";
import { Box, Button, Typography, TextField, Switch, FormControlLabel, Grid, Chip, IconButton } from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import axios from "axios";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import Loading from "../../common/loader/loading";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

const WeeklyActivityPlanner = () => {
  const user = useSelector((state) => state.auth.user);
  const [startTime, setStartTime] = useState(() => {
    const now = new Date();
    return now.toTimeString().slice(0, 5); // Extracts HH:MM format
  });
  const [endTime, setEndTime] = useState(() => {
    const now = new Date();
    return now.toTimeString().slice(0, 5); // Extracts HH:MM format
  });
  const [allDay, setAllDay] = useState(false);
  const [plannedActivity, setPlannedActivity] = useState("");
  const [comment, setComment] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activities, setActivities] = useState([]); // Tasks after filtering and sorting
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  function getWeekOfMonth(dateString) {
    const date = new Date(dateString);
    const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const dayOfWeek = startOfMonth.getDay(); // Day of the week for the 1st of the month
    const adjustedDate = date.getDate() + dayOfWeek;
    return Math.ceil(adjustedDate / 7); // Calculate week number
  }

  const formatDateToWeekday = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  };

  const fetchActivity = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/activity/activitiesAll");
      setActivities(response.data.activities);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
  }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/activity/activities/${id}`);
      // Remove it from local state after deletion
      setActivities((prev) => prev.filter((a) => a.id !== id));
    } catch (error) {
      console.error("Error deleting activity:", error);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setPlannedActivity("");
    setStartTime(new Date().toTimeString().slice(0, 5));
    setEndTime(new Date().toTimeString().slice(0, 5));
    setAllDay(false);
    setComment("");
  };

  const handleSave = async () => {
    const activityData = {
      date: selectedDate.toISOString().split("T")[0],
      startTime,
      endTime,
      allDay,
      plannedActivity,
      comment,
      completedBy: user.username,
    };

    try {
      const response = await axios.post("/api/activity/activities", activityData);
      console.log("Activity created successfully:", response.data);
    } catch (error) {
      console.error("Error creating activity:", error);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Box sx={{ p: 4 }}>
      <Chip label='Weekly Tasks' sx={{ backgroundColor: "#F2F6FF", color: "#1751D0", fontWeight: "bold" }} />
      <Box>
        <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold" }}>Activity Planner</Typography>
        <Typography variant='subtitle2' sx={{ color: "#757575" }}>
          Date: {new Date().toLocaleDateString()} &nbsp; • &nbsp;{" "}
          <span style={{ color: "#1751D0" }}>{user.username}</span>
        </Typography>
      </Box>

      <Typography variant='h6' sx={{ color: "#1751D0", mb: 2 }}>
        Week {getWeekOfMonth(selectedDate)} &nbsp;
        <span style={{ color: "#333", fontSize: "16px" }}>
          {selectedDate?.toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </span>
      </Typography>

      <Grid container spacing={4}>
        {/* LEFT SIDE FORM */}
        <Grid item xs={12} md={7}>
          <TextField
            fullWidth
            label='Activity Name'
            value={plannedActivity}
            onChange={(e) => setPlannedActivity(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            Time
          </Typography>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={5}>
              <TextField
                type='time'
                fullWidth
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                disabled={allDay}
              />
            </Grid>
            <Grid item xs={1} sx={{ textAlign: "center" }}>
              <ArrowRightAltIcon />
            </Grid>
            <Grid item xs={5}>
              <TextField
                type='time'
                fullWidth
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={allDay}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />}
                label='All day'
              />
            </Grid>
          </Grid>

          <TextField
            label='Description'
            multiline
            rows={4}
            fullWidth
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{ mt: 2 }}
          />
        </Grid>

        {/* RIGHT SIDE ACTIVITY LIST */}
        <Grid item xs={12} md={5}>
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
            <Typography variant='subtitle1' fontWeight='bold'>
              Today
            </Typography>
            {/* <Box>
              <Button size='small'>&lt;</Button>
              <Button size='small'>&gt;</Button>
              <Button size='small'>+</Button>
            </Box> */}
          </Box>
          {Array.isArray(activities) &&
            activities.map((activity, index) => (
              <Box key={index} sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2, mb: 2 }}>
                <Box display='flex' justifyContent='space-between'>
                  <Typography fontWeight='bold'>{activity?.planned_activity}</Typography>
                  <Box>
                    <IconButton color='primary' onClick={() => handleDelete(activity.id)}>
                      <DeleteIcon />
                    </IconButton>

                    <IconButton
                      color='primary'
                      onClick={() => {
                        setSelectedDate(new Date(activity.created_at));
                        setEditingId(activity.id);
                        setPlannedActivity(activity.planned_activity || "");
                        setStartTime(activity.start_time || "");
                        setEndTime(activity.end_time || "");
                        setAllDay(activity.all_day || false);
                        setComment(activity.comment || "");
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: "14px", color: "#555" }}>
                  {formatDateToWeekday(activity.created_at)}
                  <br />
                  {activity.all_day ? "All Day" : `${activity.start_time} - ${activity.end_time}`}
                </Typography>

                <Typography sx={{ mt: 1, fontSize: "14px", color: "#666" }}>{activity?.comment}</Typography>
              </Box>
            ))}
        </Grid>
      </Grid>

      {/* BOTTOM BUTTONS */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}>
        <Button variant='outlined'>Close</Button>
        <Button variant='contained' onClick={handleSave}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default WeeklyActivityPlanner;
